import { useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'
import classNames from 'classnames'
import { Link } from '~/components'
import { Image } from '~/components/Image'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { convertProductResourceUrl } from '~/helpers'
import { usePage } from '~/hooks'
import styles from './ProductDashboardColorSwatch.module.scss'
import {
  ProductColorSwatchVariant,
  ProductDashboardColorSwatchProps,
} from './ProductDashboardColorSwatch.types'

const DEFAULT_LABEL = 'Select colour'
const DEFAULT_INSTRUCTIONS =
  'Select a colour to open its product page. Screen colours may vary from the actual product.'

const getColorCountLabel = (count: number) => {
  return `${count} ${count === 1 ? 'colour' : 'colours'}`
}

const getStockLabel = (inStock: boolean) => {
  return inStock ? 'In Stock' : 'Out of Stock'
}

/*
 * The header block previews stock on hover, which a screen reader never sees,
 * so each swatch carries its own stock in its link label.
 */
const getSwatchLabel = (variant: ProductColorSwatchVariant) => {
  if (variant.inStock === undefined) {
    return variant.title
  }

  return `${variant.title} - ${getStockLabel(variant.inStock)}`
}

export const ProductDashboardColorSwatch = ({
  label = DEFAULT_LABEL,
  instructions = DEFAULT_INSTRUCTIONS,
  variants,
}: ProductDashboardColorSwatchProps) => {
  const { siteCode } = usePage()
  const [hoveredVariant, setHoveredVariant] =
    useState<ProductColorSwatchVariant | null>(null)

  const selectedVariant = variants?.find((variant) => {
    return variant.selected
  })

  /*
   * Hovering a swatch previews that colour in the header, so its stock reads
   * from the same block as the selected colour's rather than a second one.
   */
  const previewedVariant = hoveredVariant ?? selectedVariant

  if (!variants?.length) {
    return null
  }

  return (
    <div className={classNames(styles.root, 'mt-4')}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          {label && (
            <Typography
              tag={TypographyTag.div}
              variant={TypographyVariant.BodySmallBold}
              className={styles.label}>
              {label}
            </Typography>
          )}
          {previewedVariant && (
            <div className={styles.selectedTitleRow}>
              <Typography
                tag={TypographyTag.div}
                variant={TypographyVariant.BodyLargeBold}
                className={styles.selectedTitle}>
                {previewedVariant.title}
              </Typography>
              {previewedVariant.inStock !== undefined && (
                <div
                  className={classNames(styles.stockStatus, {
                    [styles.stockStatusOutOfStock]: !previewedVariant.inStock,
                  })}>
                  <div className={styles.stockStatusIcon} />
                  <Typography
                    tag={TypographyTag.span}
                    variant={TypographyVariant.BodySmallBold}
                    className={styles.stockStatusLabel}>
                    {getStockLabel(previewedVariant.inStock)}
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
        <Typography
          tag={TypographyTag.span}
          variant={TypographyVariant.BodySmallBold}
          className={styles.count}>
          {getColorCountLabel(variants.length)}
        </Typography>
      </div>
      <ul className={styles.swatchList}>
        {variants.map((variant) => {
          return (
            <li key={variant.url}>
              <Link
                href={variant.url}
                aria-label={getSwatchLabel(variant)}
                aria-current={variant.selected ? 'page' : undefined}
                className={classNames(styles.swatch, {
                  [styles.swatchSelected]: variant.selected,
                })}
                onMouseEnter={() => {
                  setHoveredVariant(variant)
                }}
                onMouseLeave={() => {
                  setHoveredVariant(null)
                }}
                onFocus={() => {
                  setHoveredVariant(variant)
                }}
                onBlur={() => {
                  setHoveredVariant(null)
                }}>
                {variant.color || !variant.thumbnail?.src ? (
                  <span
                    className={styles.swatchColor}
                    style={{ background: variant.color }}
                  />
                ) : (
                  <Image
                    src={convertProductResourceUrl(
                      variant.thumbnail.src,
                      siteCode
                    )}
                    width={variant.thumbnail.width}
                    height={variant.thumbnail.height}
                    alt={variant.thumbnail.altText}
                    className={styles.swatchImage}
                  />
                )}
              </Link>
            </li>
          )
        })}
      </ul>
      {instructions && (
        <div className={styles.instructions}>
          <IconInfoCircle
            size={18}
            color='var(--color-pdp-teal)'
            className={styles.instructionsIcon}
          />
          <Typography
            tag={TypographyTag.div}
            variant={TypographyVariant.BodySmall}
            className={styles.instructionsText}>
            {instructions}
          </Typography>
        </div>
      )}
    </div>
  )
}
